import { createSelector } from "@reduxjs/toolkit";
import { selectFilteredIssuesOnBoard } from "./issueSelectors";
import { IIssueItemStat } from "@mctypes/global.types";

const selectNrOfIssues = createSelector([selectFilteredIssuesOnBoard], (issues) => {
  return issues.length;
});

const selectNrOfActiveIssues = createSelector([selectFilteredIssuesOnBoard], (issues) => {
  return issues.filter((issue) => issue.status !== "Fullført" && issue.status !== "Utgår").length;
});

const selectCompletedLast14Days = createSelector([selectFilteredIssuesOnBoard], (issues) => {
  const now = new Date();
  const fourteenDaysAgo = new Date(now);
  fourteenDaysAgo.setDate(now.getDate() - 14);

  return issues.filter((issue) => {
    if (issue.dates?.completed != null) {
      const completedDate = new Date(issue.dates.completed);
      return completedDate >= fourteenDaysAgo && completedDate <= now;
    }
    return false;
  }).length;
});

const selectCreatedLast14Days = createSelector([selectFilteredIssuesOnBoard], (issues) => {
  const now = new Date();
  const fourteenDaysAgo = new Date(now);
  fourteenDaysAgo.setDate(now.getDate() - 14);

  return issues.filter((issue) => {
    if (issue.createdAt == null) return false;
    const createdDate = new Date(issue.createdAt);
    return createdDate >= fourteenDaysAgo && createdDate <= now;
  }).length;
});

const selectDueNext14Days = createSelector([selectFilteredIssuesOnBoard], (issues) => {
  const now = new Date();
  const fourteenDaysFromNow = new Date(now);
  fourteenDaysFromNow.setDate(now.getDate() + 14);

  return issues.filter((issue) => {
    if (issue.status === "Fullført" || issue.status === "Utgår") return false;
    if (issue.dates?.due == null) return false;
    const dueDate = new Date(issue.dates.due);
    return dueDate >= now && dueDate <= fourteenDaysFromNow;
  }).length;
});

const selectAggregatedHours = createSelector(
  [selectFilteredIssuesOnBoard],
  (issues): { hours: { totalOrignalEstimate: number; totalRemaining: number; totalCompleted: number } } => {
    const totals = issues.reduce(
      (acc, issue) => {
        acc.totalOrignalEstimate += issue.hours?.orignalEstimate ?? 0;
        acc.totalRemaining += issue.hours?.remaining ?? 0;
        acc.totalCompleted += issue.hours?.completed ?? 0;
        return acc;
      },
      { totalOrignalEstimate: 0, totalRemaining: 0, totalCompleted: 0 },
    );
    return { hours: totals };
  },
);

const selectLast10Created = createSelector([selectFilteredIssuesOnBoard], (issues) => {
  return issues
    .filter((issue) => issue.createdAt != null)
    .slice()
    .sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime())
    .slice(0, 10);
});

export const selectIssueItemStat = createSelector(
    [
        selectNrOfIssues,
        selectNrOfActiveIssues,
        selectCreatedLast14Days,
        selectCompletedLast14Days,
        selectDueNext14Days,
        selectAggregatedHours,
        selectLast10Created,
    ],
    (count, isOpen, createdLast14Days, completedLast14Days, dueNext14Days, aggregatedHours, last10Created): IIssueItemStat => ({
        count,
        isOpen,
        createdLast14Days,
        completedLast14Days,
        dueNext14Days,
        statusChartData: [],
        last10Created,
        trend: undefined,
        hours: aggregatedHours.hours,
    }),
);
